import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Panel } from "primereact/panel";
import { Card } from "primereact/card";
import { Avatar } from "primereact/avatar";
import { ReactNode } from "react";
import { IBacenScoreSCR } from "../../../interface/BacenScoreSCR";
import { currencyFormatter, percentFormatter } from "../../../utils/formatters";
import { TextField } from "../../../components/TextField";
import { Score } from "../../../components/Score";
import { BarScore } from "../../../components/BarScore";

const PercentBodyTemplate = (data: any) => {
  return <p>{percentFormatter(String(data?.PERCENTUAL))}</p>;
};

const CurrencyBodyTemplate = (data: any) => {
  return <p>{currencyFormatter(String(data?.VALOR))}</p>;
};

interface PanelItemProps {
  value?: any;
  title?: ReactNode;
  subtitle?: string;
  headerValue?: string;
  headerPercent?: string;
  items?: any[];
}

const PanelItem = ({
  value,
  title,
  subtitle,
  headerValue,
  headerPercent,
}: PanelItemProps) => {
  return (
    <Panel id="panel-item-bacen-score" header={title}>
      {subtitle && <p>{subtitle}</p>}

      <DataTable
        value={value ?? []}
        size="small"
        tableStyle={{ minWidth: "50rem" }}
      >
        <Column field="DESCRICAO" header="Descrição" />
        <Column
          field="VALOR"
          header={currencyFormatter(headerValue ?? "0.00")}
          body={CurrencyBodyTemplate}
        />
        <Column
          field="PERCENTUAL"
          header={percentFormatter(headerPercent ?? "0.00")}
          body={PercentBodyTemplate}
        />
      </DataTable>
    </Panel>
  );
};

export const BacenScoreSCRPage = (data: IBacenScoreSCR) => {

  const isEmptyData = !data || Object.keys(data).length === 0;

  if (isEmptyData) {
    return (
      <div style={{ width: "98%" }}>
        <Panel header="Dado Comportamental de Pagamento">
          <Card style={{backgroundColor: "rgba(239, 68, 68, 0.1)", color: "rgb(239, 68, 68)", border: "1px solid rgb(239, 68, 68)"}}>
              <div className="flex-score align-items-center">
                  <Avatar style={{backgroundColor: "rgba(239, 68, 68, 0.1)"}} icon="pi pi-exclamation-triangle" size="xlarge" shape="circle" />
                  <span style={{paddingLeft: "1rem"}}><b>ERRO NA CONSULTA</b></span>
              </div> 
          </Card>
        </Panel>
      </div>
    );
  }

  const score = data?.RELATORIO_SCR?.CONSOLIDADO ?? null;
  const operation = data?.RELATORIO_SCR?.OPERACOES ?? null;
  const report = data?.RELATORIO_SCR ?? null;
  const getValue = (value?: string): string => value || "Não informado";

  const getOperationTotal = (modalidade: string, subModalidade: string): string => {
    if (!operation || !Array.isArray(operation)) return "0";

    let total = 0;
    for (const op of operation) {
      const mod = op?.MODALIDADE?.DESCRICAO?.toUpperCase().trim() || "";
      const sub = op?.SUB_MODALIDADE?.DESCRICAO?.toUpperCase().trim() || "";

      const matchMod = modalidade.toUpperCase().split('//').some((m: string) => mod.includes(m.trim()));

      const normalize = (str: string) => str.replace(/[-–—]/g, '').replace(/\s+/g, ' ').trim();

      const subNorm = normalize(sub);
      const searchSubNorm = normalize(subModalidade.toUpperCase());

      if (matchMod && subNorm.includes(searchSubNorm)) {
        total += Number(op.TOTAL?.replace(",", ".") || 0);
      }
    }
    return total.toString();
  };

  return (
    <div style={{ width: "98%" }}>
      <Panel header="Dado Comportamental de Pagamento">
        <Panel header="Dados da Consulta">
          <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <TextField
                label="Data de Fundação"
                value={getValue(report?.NASCIMENTO_FUNDACAO)}
              />
              <TextField
                label="Data de Início do Relacionamento"
                value={getValue(report?.DATA_INICIO_RELACIONAMENTO)}
              />
              <TextField
                label="Tipo de Documento"
                value={getValue(report?.TIPO_DOCUMENTO)}
              />
              <TextField
                label="Data Base"
                value={getValue(report?.DATABASE_CONSULTADA)}
              />
              <TextField
                label="Limite de Crédito"
                value={currencyFormatter(report?.CONSOLIDADO?.LIMITE_CREDITO?.VALOR)}
              />
              <TextField
                label="Prejuízo"
                value={currencyFormatter(report?.CONSOLIDADO?.PREJUIZO?.VALOR)}
              />
            </div>
            <div>
              <TextField
                label="Coobrigação Recebida"
                value={currencyFormatter(report?.COOBRIGACAO_RECEBIDA)}
              />
              <TextField
                label="Percentual de Documentos Processados"
                value={percentFormatter(
                  report?.PERCENTUAL_DOCUMENTOS_PROCESSADOS
                )}
              />
              <TextField
                label="Número de Instituições"
                value={getValue(report?.QUANTIDADE_INSTITUICOES)}
              />
              <TextField
                label="Valor Crédito Vencido"
                value={currencyFormatter(report?.CONSOLIDADO?.CREDITO_VENCIDO?.VALOR)}
              />
              <TextField
                label="Valor Crédito A Vencer"
                value={currencyFormatter(report?.CONSOLIDADO?.CREDITO_AVENCER?.VALOR)}
              />
            </div>
          </section>

          <br />
          <h3 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "1rem" }}>Limites</h3>
          <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <TextField label="Cheque Especial" value={currencyFormatter(getOperationTotal("LIMITE", "CHEQUE ESPECIAL"))} />
              <TextField label="Cartão de Crédito" value={currencyFormatter(getOperationTotal("LIMITE", "CARTAO DE CREDITO"))} />
            </div>
            <div>
              <TextField label="Capital de Giro" value={currencyFormatter(getOperationTotal("LIMITE", "CAPITAL DE GIRO"))} />
              <TextField label="Descontos" value={currencyFormatter(getOperationTotal("LIMITE", "DESCONTOS"))} />
            </div>
          </section>

          <h3 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "1rem" }}>Empréstimos</h3>
          <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <TextField label="Cheque Especial" value={currencyFormatter(getOperationTotal("EMPRESTIMO", "CHEQUE ESPECIAL"))} />
              <TextField label="Capital de Giro com prazo vencimento superior 365 D" value={currencyFormatter(getOperationTotal("EMPRESTIMO", "CAPITAL DE GIRO COM PRAZO VENCIMENTO SUPERIOR 365 D"))} />
            </div>
            <div>
              <TextField label="Capital de Giro com teto rotativo" value={currencyFormatter(getOperationTotal("EMPRESTIMO", "CAPITAL DE GIRO COM TETO ROTATIVO"))} />
            </div>
          </section>

          <h3 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "1rem" }}>Outros Créditos</h3>
          <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <TextField label="Cartão de Crédito - Compra a vista e parcelado lojista" value={currencyFormatter(getOperationTotal("OUTROS CREDITOS", "CARTAO DE CREDITO - COMPRA A VISTA E PARCELADO LOJISTA"))} />
            </div>
            <div></div>
          </section>

          <h3 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "1rem" }}>Financiamentos</h3>
          <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <TextField label="Aquisição de Bens - Veículos Automotores" value={currencyFormatter(getOperationTotal("FINANCIAMENTO", "AQUISICAO DE BENS - VEICULOS AUTOMOTORES"))} />
            </div>
            <div>
              <TextField label="Financiamento de Projeto" value={currencyFormatter(getOperationTotal("FINANCIAMENTO", "FINANCIAMENTO DE PROJETO"))} />
            </div>
          </section>

          <h3 style={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "1rem" }}>Títulos Descontados / Direitos Creditórios Descontados</h3>
          <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div>
              <TextField label="Desconto de Duplicatas" value={currencyFormatter(getOperationTotal("TITULOS DESCONTADOS // DIREITOS CREDITORIOS DESCONTADOS", "DESCONTO DE DUPLICATAS"))} />
            </div>
            <div></div>
          </section>
        </Panel>

        <br />

        <Panel header="Score">
          <div style={{ display: "flex", gap: "40px" }}>
            <div
              className="scrScore"
            >
              <Score
                score={report?.SCORE?.PONTUACAO ? report?.SCORE?.PONTUACAO : '0'}
                range={report?.SCORE?.FAIXA}
              />
            </div>

            <BarScore
              score={report?.SCORE?.PONTUACAO}
              rangeColor={report?.SCORE?.FAIXA}
            />
          </div>

          <p style={{ fontWeight: "bold" }}>
            IMPORTANTE:{" "}
            <span style={{ fontWeight: "normal", fontStyle: "italic" }}>
              a decisão de aprovação ou não do crédito é de exclusiva
              responsabilidade do concedente. as informações prestadas pelo
              provedor tem como objevo subsidiar essas decisões e, em hipótese
              alguma devem ser ulizadas como justicativa, pelo concedente do
              crédito, para a tomada da referida decisão.
            </span>
          </p>
        </Panel>

        <br />

        {!!data?.RELATORIO_SCR && (
          <>
            <PanelItem
              title="Crédito Vencido"
              value={score?.CREDITO_VENCIDO?.OPERACOES}
              headerValue={score?.CREDITO_VENCIDO?.VALOR}
              headerPercent={score?.CREDITO_VENCIDO?.PERCENTUAL}
            />

            <br />

            <PanelItem
              title="Crédito a Vencer"
              value={score?.CREDITO_AVENCER?.OPERACOES}
              headerValue={score?.CREDITO_AVENCER?.VALOR}
              headerPercent={score?.CREDITO_AVENCER?.PERCENTUAL}
            />

            <br />

            <PanelItem
              title="Limite de Crédito"
              value={score?.LIMITE_CREDITO?.OPERACOES}
              headerValue={score?.LIMITE_CREDITO?.VALOR}
              headerPercent={score?.LIMITE_CREDITO?.PERCENTUAL}
            />

            <br />

            <PanelItem
              title="Prejuizo"
              value={score?.PREJUIZO?.OPERACOES}
              headerValue={score?.PREJUIZO?.VALOR}
              headerPercent={score?.PREJUIZO?.PERCENTUAL}
            />

            <br />

            {operation?.map((item, index) => {

              return (
                <Panel key={index} header={item?.MODALIDADE?.DESCRICAO}>
                  <p>{item?.SUB_MODALIDADE?.DESCRICAO}</p>

                  {/* {code === proxCode && } */}
                  <DataTable
                    value={item?.VENCIMENTOS ?? []}
                    size="small"
                    tableStyle={{ minWidth: "50rem" }}
                  >
                    <Column field="DESCRICAO" header="Descrição" />
                    <Column
                      field="VALOR"
                      header={currencyFormatter(item?.TOTAL ?? "0.00")}
                      body={CurrencyBodyTemplate}
                    />
                    <Column
                      field="PERCENTUAL"
                      header={percentFormatter(item?.PERCENTUAL ?? "0.00")}
                      body={PercentBodyTemplate}
                    />
                  </DataTable>
                </Panel>
              );
            })}
          </>
        )}
        {!data?.RELATORIO_SCR && (
          <p>Nenhum dado encontrado para esta consulta</p>
        )}
      </Panel>
    </div>
  );
};